import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, Input, Select } from '@tkpod/ui';
import { ApiClientError } from '@tkpod/api-client';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

interface ProviderMapping {
  id: string;
  providerId: string;
  providerName: string;
  providerSlug: string;
  providerStatus: string;
  externalSku: string | null;
  externalProductId: string | null;
  costOverride: string | null;
  isDefault: boolean;
  priority: number;
  isActive: boolean;
  createdAt: string;
}

interface ProviderOption {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface VariantMapping {
  blankVariantId: string;
  sku: string;
  options: Array<{ typeSlug: string; valueName: string }>;
  variantMappingId: string | null;
  externalSku: string | null;
  externalVariantId: string | null;
  costOverride: string | null;
  isActive: boolean;
}

export function CatalogProvidersTab({ templateId, onDirtyChange }: { templateId: string; onDirtyChange?: (dirty: boolean) => void }) {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedMapping, setExpandedMapping] = useState<string | null>(null);
  const [editingMapping, setEditingMapping] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const isDirty = showAddModal || editingMapping !== null;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const mappingsKey = ['provider-mappings', templateId];

  const { data: mappings = [], isLoading, isError, error } = useQuery<ProviderMapping[]>({
    queryKey: mappingsKey,
    queryFn: async () => {
      const res = await api.get<{ data: ProviderMapping[] }>(`/api/v1/admin/catalog/${templateId}/providers`);
      return res.data;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: mappingsKey });

  const deleteMutation = useMutation({
    mutationFn: (mappingId: string) =>
      api.delete(`/api/v1/admin/catalog/${templateId}/providers/${mappingId}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
      toast('Provider removed.');
    },
    onError: () => {
      setConfirmDelete(null);
      toast('Failed to remove provider.', 'error');
    },
  });

  if (isLoading) {
    return (
      <Card>
        <div className="animate-pulse px-6 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-24 rounded bg-surface-tertiary" />
            <div className="h-8 w-28 rounded bg-surface-tertiary" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-md border border-border px-4 py-3">
              <div className="h-4 w-4 rounded bg-surface-tertiary" />
              <div className="h-4 w-32 rounded bg-surface-tertiary" />
              <div className="h-5 w-20 rounded bg-surface-tertiary" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="py-12 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger-50">
            <svg className="h-5 w-5 text-danger-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-text-secondary">Failed to load providers</p>
          <p className="mt-1 text-xs text-text-tertiary">{getErrorMessage(error, 'An unexpected error occurred.')}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => invalidate()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-text-primary">Providers</h3>
            <Badge variant="default" size="sm">{mappings.length} mapped</Badge>
          </div>
          <Button size="sm" onClick={() => setShowAddModal(true)}>+ Add Provider</Button>
        </div>

        {mappings.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-text-tertiary">
            No providers mapped to this template yet. Add a provider to enable fulfillment routing.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {mappings.map((m) => (
              <ProviderMappingCard
                key={m.id}
                mapping={m}
                templateId={templateId}
                isExpanded={expandedMapping === m.id}
                isEditing={editingMapping === m.id}
                showDeleteConfirm={confirmDelete === m.id}
                onToggleExpand={() => setExpandedMapping(expandedMapping === m.id ? null : m.id)}
                onEdit={() => setEditingMapping(editingMapping === m.id ? null : m.id)}
                onEditDone={() => { setEditingMapping(null); invalidate(); }}
                onDeleteRequest={() => setConfirmDelete(m.id)}
                onDeleteConfirm={() => deleteMutation.mutate(m.id)}
                onDeleteCancel={() => setConfirmDelete(null)}
                isDeleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}
      </Card>

      {showAddModal && (
        <AddProviderModal
          templateId={templateId}
          existingProviderIds={mappings.map((m) => m.providerId)}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); invalidate(); }}
        />
      )}
    </div>
  );
}

// ─── Provider Mapping Card ──────────────────────────────────────────────────

function ProviderMappingCard({
  mapping,
  templateId,
  isExpanded,
  isEditing,
  showDeleteConfirm,
  onToggleExpand,
  onEdit,
  onEditDone,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
  isDeleting,
}: {
  mapping: ProviderMapping;
  templateId: string;
  isExpanded: boolean;
  isEditing: boolean;
  showDeleteConfirm: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onEditDone: () => void;
  onDeleteRequest: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-3 text-left"
          onClick={onToggleExpand}
        >
          <svg
            className={`h-4 w-4 text-text-tertiary transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
          <span className="font-medium text-sm text-text-primary">{mapping.providerName}</span>
          <Badge variant="default" size="sm">Priority: {mapping.priority}</Badge>
          {mapping.isDefault && <Badge variant="success" size="sm">Default</Badge>}
          {!mapping.isActive && <Badge variant="warning" size="sm">Inactive</Badge>}
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onEdit}>
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          {showDeleteConfirm ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-danger-600 mr-1">Remove?</span>
              <Button size="sm" variant="danger" onClick={onDeleteConfirm} disabled={isDeleting}>
                {isDeleting ? '...' : 'Yes'}
              </Button>
              <Button size="sm" variant="outline" onClick={onDeleteCancel}>No</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={onDeleteRequest}>Remove</Button>
          )}
        </div>
      </div>

      {/* Inline info */}
      <div className="mt-2 ml-7 flex flex-wrap gap-x-6 gap-y-1 text-xs text-text-secondary">
        <span>External SKU: {mapping.externalSku || '—'}</span>
        <span>External Product ID: {mapping.externalProductId || '—'}</span>
        <span>Cost Override: {mapping.costOverride ? `$${mapping.costOverride}` : '—'}</span>
      </div>

      {/* Edit Form */}
      {isEditing && (
        <EditMappingForm
          mapping={mapping}
          templateId={templateId}
          onDone={onEditDone}
        />
      )}

      {/* Variant Mappings Accordion */}
      {isExpanded && !isEditing && (
        <VariantMappingsTable templateId={templateId} mappingId={mapping.id} />
      )}
    </div>
  );
}

// ─── Edit Mapping Form ──────────────────────────────────────────────────────

function EditMappingForm({
  mapping,
  templateId,
  onDone,
}: {
  mapping: ProviderMapping;
  templateId: string;
  onDone: () => void;
}) {
  const [priority, setPriority] = useState(mapping.priority.toString());
  const [externalSku, setExternalSku] = useState(mapping.externalSku ?? '');
  const [externalProductId, setExternalProductId] = useState(mapping.externalProductId ?? '');
  const [costOverride, setCostOverride] = useState(mapping.costOverride ?? '');
  const [isDefault, setIsDefault] = useState(mapping.isDefault);

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/v1/admin/catalog/${templateId}/providers/${mapping.id}`, body),
    onSuccess: () => { onDone(); toast('Provider mapping updated.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to update provider mapping.', 'error'); },
  });

  const handleSave = () => {
    updateMutation.mutate({
      priority: parseInt(priority, 10) || 0,
      externalSku: externalSku || undefined,
      externalProductId: externalProductId || undefined,
      costOverride: costOverride ? parseFloat(costOverride) : undefined,
      isDefault,
    });
  };

  return (
    <div className="mt-3 ml-7 space-y-3 rounded-md border border-border bg-surface-secondary p-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-text-secondary">Priority</label>
          <Input
            type="number"
            min="0"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-text-secondary">Cost Override ($)</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={costOverride}
            onChange={(e) => setCostOverride(e.target.value)}
            placeholder="Leave empty for no override"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-text-secondary">External SKU</label>
          <Input
            value={externalSku}
            onChange={(e) => setExternalSku(e.target.value)}
            placeholder="e.g. TKH-BC3001"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-text-secondary">External Product ID</label>
          <Input
            value={externalProductId}
            onChange={(e) => setExternalProductId(e.target.value)}
            placeholder="Provider's product ID"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`default-${mapping.id}`}
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
        />
        <label htmlFor={`default-${mapping.id}`} className="text-xs text-text-secondary">
          Default provider for this template
        </label>
      </div>
      {updateMutation.isError && (
        <p className="text-xs text-danger-600">
          {getErrorMessage(updateMutation.error, 'Failed to update mapping.')}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

// ─── Variant Mappings Table ─────────────────────────────────────────────────

function VariantMappingsTable({
  templateId,
  mappingId,
}: {
  templateId: string;
  mappingId: string;
}) {
  const queryClient = useQueryClient();
  const [editedSkus, setEditedSkus] = useState<Record<string, string>>({});
  const [autoPrefix, setAutoPrefix] = useState('');

  const variantMappingsKey = ['variant-mappings', templateId, mappingId];

  const { data: variantMappings = [], isLoading } = useQuery<VariantMapping[]>({
    queryKey: variantMappingsKey,
    queryFn: async () => {
      const res = await api.get<{ data: VariantMapping[] }>(
        `/api/v1/admin/catalog/${templateId}/providers/${mappingId}/variant-mappings`,
      );
      return res.data;
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (mappings: Array<{ blankVariantId: string; externalSku?: string }>) =>
      api.patch(
        `/api/v1/admin/catalog/${templateId}/providers/${mappingId}/variant-mappings`,
        { mappings },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: variantMappingsKey });
      setEditedSkus({});
      toast('Variant mappings saved.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to save variant mappings.', 'error');
    },
  });

  const handleSkuChange = (variantId: string, value: string) => {
    setEditedSkus((prev) => ({ ...prev, [variantId]: value }));
  };

  const handleSaveAll = () => {
    const changedMappings = Object.entries(editedSkus)
      .filter(([variantId, sku]) => {
        const original = variantMappings.find((vm) => vm.blankVariantId === variantId);
        return sku !== (original?.externalSku ?? '');
      })
      .map(([blankVariantId, externalSku]) => ({
        blankVariantId,
        externalSku: externalSku || undefined,
      }));

    if (changedMappings.length === 0) return;
    bulkMutation.mutate(changedMappings);
  };

  const handleAutoFill = () => {
    if (!autoPrefix) return;
    const newSkus: Record<string, string> = {};
    for (const vm of variantMappings) {
      const suffix = vm.sku.split('-').slice(1).join('-');
      newSkus[vm.blankVariantId] = `${autoPrefix}-${suffix || vm.sku}`;
    }
    setEditedSkus(newSkus);
  };

  const hasChanges = Object.entries(editedSkus).some(([variantId, sku]) => {
    const original = variantMappings.find((vm) => vm.blankVariantId === variantId);
    return sku !== (original?.externalSku ?? '');
  });

  if (isLoading) {
    return (
      <div className="mt-3 ml-7 animate-pulse space-y-2 py-4">
        <div className="h-4 w-40 rounded bg-surface-tertiary" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 w-full rounded bg-surface-tertiary" />
        ))}
      </div>
    );
  }

  if (variantMappings.length === 0) {
    return (
      <div className="mt-3 ml-7 rounded-md border border-border p-4 text-center text-xs text-text-tertiary">
        No variants found for this template. Generate variants first in the Variants tab.
      </div>
    );
  }

  return (
    <div className="mt-3 ml-7 space-y-3">
      {/* Auto-fill toolbar */}
      <div className="flex items-center gap-2">
        <Input
          value={autoPrefix}
          onChange={(e) => setAutoPrefix(e.target.value)}
          placeholder="Prefix (e.g. TKH)"
          className="max-w-48"
        />
        <Button size="sm" variant="outline" onClick={handleAutoFill} disabled={!autoPrefix}>
          Auto-fill SKUs
        </Button>
        {hasChanges && (
          <Button size="sm" onClick={handleSaveAll} disabled={bulkMutation.isPending}>
            {bulkMutation.isPending ? 'Saving...' : 'Save All'}
          </Button>
        )}
      </div>

      {bulkMutation.isError && (
        <p className="text-xs text-danger-600">
          {getErrorMessage(bulkMutation.error, 'Failed to save variant mappings.')}
        </p>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-surface-secondary">
              <th className="px-3 py-2 text-left font-medium text-text-secondary">Internal SKU</th>
              <th className="px-3 py-2 text-left font-medium text-text-secondary">Options</th>
              <th className="px-3 py-2 text-left font-medium text-text-secondary">External SKU</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {variantMappings.map((vm) => {
              const currentSku = editedSkus[vm.blankVariantId] ?? vm.externalSku ?? '';
              const optionLabel = vm.options.map((o) => o.valueName).join(' / ') || '—';

              return (
                <tr key={vm.blankVariantId} className="hover:bg-surface-secondary/50">
                  <td className="px-3 py-2 font-mono text-text-primary">{vm.sku}</td>
                  <td className="px-3 py-2 text-text-secondary">{optionLabel}</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={currentSku}
                      onChange={(e) => handleSkuChange(vm.blankVariantId, e.target.value)}
                      className="w-full rounded border border-border bg-surface px-2 py-1 text-xs text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      placeholder="External SKU"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Add Provider Modal ─────────────────────────────────────────────────────

function AddProviderModal({
  templateId,
  existingProviderIds,
  onClose,
  onSuccess,
}: {
  templateId: string;
  existingProviderIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [priority, setPriority] = useState('0');
  const [externalSku, setExternalSku] = useState('');
  const [externalProductId, setExternalProductId] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [handleEsc]);

  const { data: providers = [], isLoading: providersLoading } = useQuery<ProviderOption[]>({
    queryKey: ['all-providers'],
    queryFn: async () => {
      const res = await api.get<{ data: ProviderOption[] }>('/api/v1/admin/providers');
      return res.data;
    },
  });

  const availableProviders = providers.filter((p) => !existingProviderIds.includes(p.id));

  const addMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/api/v1/admin/catalog/${templateId}/providers`, body),
    onSuccess: () => { onSuccess(); toast('Provider added.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to add provider.', 'error'); },
  });

  const handleSubmit = () => {
    if (!selectedProviderId) return;
    addMutation.mutate({
      providerId: selectedProviderId,
      priority: parseInt(priority, 10) || 0,
      externalSku: externalSku || undefined,
      externalProductId: externalProductId || undefined,
      isDefault,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-surface shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-base font-semibold text-text-primary">Add Provider</h3>
          <button type="button" onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-text-secondary">Provider</label>
            {providersLoading ? (
              <p className="mt-1 text-xs text-text-tertiary">Loading providers...</p>
            ) : availableProviders.length === 0 ? (
              <p className="mt-1 text-xs text-text-tertiary">
                All providers are already mapped to this template.
              </p>
            ) : (
              <Select
                value={selectedProviderId}
                onChange={(e) => setSelectedProviderId(e.target.value)}
                options={[
                  { value: '', label: 'Select a provider...' },
                  ...availableProviders.map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.slug})`,
                  })),
                ]}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-text-secondary">Priority</label>
              <Input
                type="number"
                min="0"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-text-secondary">External SKU</label>
              <Input
                value={externalSku}
                onChange={(e) => setExternalSku(e.target.value)}
                placeholder="e.g. TKH-BC3001"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">External Product ID</label>
            <Input
              value={externalProductId}
              onChange={(e) => setExternalProductId(e.target.value)}
              placeholder="Provider's product identifier"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="add-default"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="add-default" className="text-xs text-text-secondary">
              Set as default provider for this template
            </label>
          </div>

          {addMutation.isError && (
            <p className="text-xs text-danger-600">
              {getErrorMessage(addMutation.error, 'Failed to add provider.')}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!selectedProviderId || addMutation.isPending}
            >
              {addMutation.isPending ? 'Adding...' : 'Add Provider'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
